import RegularText from '@/components/RegularText';
import { BlockStack, Button, Card, Collapsible, Icon, InlineStack, List, Text } from '@shopify/polaris';
import { QuestionCircleIcon } from '@shopify/polaris-icons';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PricingFAQsStyled } from './styled';

interface FAQItem {
  label: string;
  extend: boolean;
  content: React.ReactNode;
}

const PricingFAQs: FC = () => {
  const { t } = useTranslation('pricing');

  const initialData: FAQItem[] = useMemo(
    () => [
      {
        label: t('free_plan_offer'),
        extend: false,
        content: <RegularText>{t('free_plan_limited_access_upgrade_options')}</RegularText>,
      },
      {
        label: t('stop_charges_without_uninstall'),
        extend: false,
        content: <RegularText>{t('free_plan_switching')}</RegularText>,
      },
      {
        label: t('can_downgrade_paid_plan'),
        extend: false,
        content: (
          <RegularText>
            {t('downgrade_access_loss_lower_plan')}
            <br />
            {t('example_key')}
            <List type="bullet">
              <List.Item>{t('premium_to_basic_downgrade')}</List.Item>
              <List.Item>{t('downgrade_free_plan_disable_features')}</List.Item>
            </List>
            {t('upgrade_access_reconfigure_settings')}
          </RegularText>
        ),
      },
      {
        label: t('settings_saved_uninstall'),
        extend: false,
        content: <RegularText>{t('app_uninstallation_data_removal')}</RegularText>,
      },
    ],
    [t],
  );

  const [listData, setListData] = useState<FAQItem[]>([]);

  useEffect(() => {
    setListData(initialData);
  }, [initialData]);

  const handleToggle = (index: number) => {
    setListData((prevData) =>
      prevData.map((item, i) => {
        if (i === index) {
          return { ...item, extend: !item.extend };
        }
        return { ...item, extend: false };
      }),
    );
  };

  return (
    <PricingFAQsStyled className="pricing-faqs-container">
      <BlockStack gap="400">
        <InlineStack gap="100" blockAlign="center">
          <span>
            <Icon source={QuestionCircleIcon} />
          </span>
          <Text as="h3" variant="headingMd">
            {t('pricing_faqs')}
          </Text>
        </InlineStack>

        <Card padding={{ xs: '400', lg: '600' }}>
          <BlockStack gap="400">
            {listData?.map((question, index) => (
              <React.Fragment key={question.label}>
                <Button size="large" fullWidth textAlign="left" onClick={() => handleToggle(index)}>
                  {question.label}
                </Button>

                <Collapsible
                  open={question.extend}
                  id="basic-collapsible"
                  transition={{ duration: '300ms', timingFunction: 'ease-in-out' }}
                >
                  <div className="pricing-faqs-content">{question.content}</div>
                </Collapsible>
              </React.Fragment>
            ))}
          </BlockStack>
        </Card>
      </BlockStack>
    </PricingFAQsStyled>
  );
};

export default PricingFAQs;
